import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Hash, CheckCircle, AlertCircle, Loader2, Trash2, ScanLine } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';
import { ScanTextInput } from '../../../../common/inputs';

interface SerialNumberReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (serialNumbers: string[]) => void;
  itemNo: string;
  itemDescription: string;
  expectedQuantity: number;
  /** Pre-registered serial numbers (e.g. when re-opening modal) */
  existingSerialNumbers?: string[];
}

export const SerialNumberReceiptModal: React.FC<SerialNumberReceiptModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  itemNo,
  itemDescription,
  expectedQuantity,
  existingSerialNumbers = []
}) => {
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);

  // Reset state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setSerialNumbers(existingSerialNumbers || []);
      setCurrentInput('');
      setError(null);
      setChecking(false);
      // Focus input after modal renders
      setTimeout(() => inputRef.current?.focus(), 100);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const registeredCount = serialNumbers.length;
  const remainingCount = expectedQuantity - registeredCount;
  const isComplete = remainingCount <= 0;

  const addSerialNumber = useCallback(async () => {
    const trimmed = currentInput.trim();
    if (!trimmed) return;

    setError(null);

    // Local duplicate check
    if (serialNumbers.includes(trimmed)) {
      setError(`Serienummer "${trimmed}" er allerede lagt til i listen`);
      return;
    }

    // Backend duplicate check (global uniqueness per item)
    setChecking(true);
    try {
      const exists = await stcTransService.checkSerialNumberExists(itemNo, trimmed);
      if (exists) {
        setError(`Serienummer "${trimmed}" er allerede registrert for vare ${itemNo}`);
        setChecking(false);
        return;
      }
    } catch (err) {
      console.error('Feil ved serienummersjekk:', err);
      // Allow adding if check fails (offline tolerance) - backend will validate again at finish
      console.warn('Kunne ikke validere serienummer mot backend, legger til lokalt');
    }
    setChecking(false);

    setSerialNumbers(prev => [...prev, trimmed]);
    setCurrentInput('');

    // Keep focus on input for continuous scanning
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [currentInput, serialNumbers, itemNo]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addSerialNumber();
    }
  };

  const removeSerialNumber = (index: number) => {
    setSerialNumbers(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const handleComplete = () => {
    if (!isComplete) {
      setError(`Du m\u00e5 registrere ${remainingCount} serienummer til`);
      return;
    }
    onComplete(serialNumbers);
  };

  // Stop all events from propagating to parent (prevents barcode scanner interference)
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Hash className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Registrer serienumre</h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress */}
        <div className="px-4 pt-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">
              Registrert: <span className="font-semibold">{registeredCount}</span> av <span className="font-semibold">{expectedQuantity}</span>
            </span>
            {isComplete ? (
              <span className="text-green-600 font-medium flex items-center gap-1">
                <CheckCircle size={16} /> Komplett
              </span>
            ) : (
              <span className="text-orange-600 font-medium">
                {remainingCount} gjenstår
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                isComplete ? 'bg-green-500' : 'bg-purple-500'
              }`}
              style={{ width: `${Math.min(100, (registeredCount / expectedQuantity) * 100)}%` }}
            />
          </div>
        </div>

        {/* Input */}
        <div className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <ScanTextInput
                ref={inputRef}
                value={currentInput}
                onChange={(e) => {
                  setCurrentInput(e.target.value);
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                onFocus={(e) => e.stopPropagation()}
                placeholder={isComplete ? 'Alle serienumre registrert' : 'Skann eller skriv serienummer...'}
                disabled={isComplete || checking}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                autoFocus
              />
            </div>
            <button
              onClick={addSerialNumber}
              disabled={!currentInput.trim() || isComplete || checking}
              className="px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {checking ? <Loader2 className="animate-spin" size={16} /> : 'Legg til'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Serial number list */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {serialNumbers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <ScanLine size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Ingen serienumre registrert ennå</p>
              <p className="text-xs mt-1">Skann eller skriv inn serienumre ovenfor</p>
            </div>
          ) : (
            <div className="space-y-1">
              {serialNumbers.map((sn, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg group"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-400 w-6 text-right">{index + 1}.</span>
                    <span className="font-mono text-sm text-gray-900">{sn}</span>
                  </div>
                  <button
                    onClick={() => removeSerialNumber(index)}
                    className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    title="Fjern serienummer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleComplete}
            disabled={!isComplete}
            className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              isComplete
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <CheckCircle size={16} />
            Bekreft {registeredCount} serienumre
          </button>
        </div>
      </div>
    </div>
  );
};
